import React, { useState, useEffect } from 'react';
import { DollarSign, Maximize2, AlertTriangle, Users, LayoutDashboard, TrendingUp, Save, X, TrendingDown } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import BuildingStack from './shared/BuildingStack';

interface Zone {
  id: string;
  name: string;
  type: 'workspace' | 'meeting' | 'collaboration' | 'amenity';
  capacity: number;
  currentOccupancy: number;
  area: number;
  costPerSqFt: number;
  utilization: number;
  adjacentZones: string[];
}

interface Department {
  id: string;
  name: string;
  headcount: number;
  currentSpace: number;
  projectedGrowth: number;
  collaborationScore: number;
  primaryZones: string[];
}

interface SimulationMetrics {
  capacityDelta: number;
  costImpact: number;
  utilizationScore: number;
  collaborationScore: number;
  congestionRisk: 'low' | 'medium' | 'high';
  recommendations: string[];
}

const SpaceOptimization: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'reclaim' | 'planning'>('reclaim');
  
  const [leaseRate, setLeaseRate] = useState(42);
  const [scenario, setScenario] = useState<'current' | 'proposed'>('current');

  const currentFloors = [
    { floor: 'Floor 5', department: 'Operations', utilization: 38, capacity: 85, sqft: 8500 },
    { floor: 'Floor 4', department: 'Engineering', utilization: 62, capacity: 100, sqft: 10200 },
    { floor: 'Floor 3', department: 'Sales', utilization: 54, capacity: 90, sqft: 9400 },
    { floor: 'Floor 2', department: 'Marketing', utilization: 48, capacity: 75, sqft: 7800 },
    { floor: 'Floor 1', department: 'Executive', utilization: 42, capacity: 60, sqft: 6200 }
  ];

  const proposedFloors = [
    { floor: 'Floor 5', department: 'Reclaimed / Sublet', utilization: 0, capacity: 0, sqft: 8500 },
    { floor: 'Floor 4', department: 'Engineering', utilization: 62, capacity: 100, sqft: 10200 },
    { floor: 'Floor 3', department: 'Sales + Marketing', utilization: 68, capacity: 120, sqft: 9400 },
    { floor: 'Floor 2', department: 'Operations', utilization: 72, capacity: 85, sqft: 7800 },
    { floor: 'Floor 1', department: 'Executive', utilization: 42, capacity: 60, sqft: 6200 }
  ];

  const currentSqft = currentFloors.reduce((sum, f) => sum + f.sqft, 0);
  const proposedSqft = proposedFloors.filter(f => f.utilization > 0).reduce((sum, f) => sum + f.sqft, 0);
  const reclaimSqft = currentSqft - proposedSqft;
  const annualSavings = reclaimSqft * leaseRate;

  const spaceTypes = [
    { type: 'Open Desks', current: 180, currentUtil: 52, proposed: 140, proposedUtil: 68, sqft: 14400 },
    { type: 'Meeting Rooms', current: 28, currentUtil: 42, proposed: 18, proposedUtil: 72, sqft: 3200 },
    { type: 'Focus Pods', current: 12, currentUtil: 68, proposed: 24, proposedUtil: 78, sqft: 1800 },
    { type: 'Collaboration', current: 6, currentUtil: 38, proposed: 8, proposedUtil: 65, sqft: 2400 },
    { type: 'Break/Amenity', current: 4, currentUtil: 55, proposed: 3, proposedUtil: 72, sqft: 1800 }
  ];

  const [zones, setZones] = useState<Zone[]>([
    {
      id: 'z1',
      name: 'Open Office North',
      type: 'workspace',
      capacity: 50,
      currentOccupancy: 35,
      area: 2000,
      costPerSqFt: 45,
      utilization: 70,
      adjacentZones: ['z2', 'z3']
    },
    {
      id: 'z2',
      name: 'Conference Center',
      type: 'meeting',
      capacity: 30,
      currentOccupancy: 15,
      area: 1000,
      costPerSqFt: 55,
      utilization: 50,
      adjacentZones: ['z1', 'z4']
    },
    {
      id: 'z3',
      name: 'Innovation Hub',
      type: 'collaboration',
      capacity: 25,
      currentOccupancy: 20,
      area: 800,
      costPerSqFt: 50,
      utilization: 80,
      adjacentZones: ['z1', 'z4']
    },
    {
      id: 'z4',
      name: 'Break Area',
      type: 'amenity',
      capacity: 20,
      currentOccupancy: 8,
      area: 600,
      costPerSqFt: 40,
      utilization: 40,
      adjacentZones: ['z2', 'z3']
    }
  ]);

  const [departments, setDepartments] = useState<Department[]>([
    {
      id: 'd1',
      name: 'Engineering',
      headcount: 30,
      currentSpace: 1200,
      projectedGrowth: 15,
      collaborationScore: 85,
      primaryZones: ['z1', 'z3']
    },
    {
      id: 'd2',
      name: 'Sales',
      headcount: 20,
      currentSpace: 800,
      projectedGrowth: 10,
      collaborationScore: 75,
      primaryZones: ['z1', 'z2']
    }
  ]);

  const [selectedZone, setSelectedZone] = useState<string | null>(null);
  const [simulationMetrics, setSimulationMetrics] = useState<SimulationMetrics>({
    capacityDelta: 0,
    costImpact: 0,
    utilizationScore: 75,
    collaborationScore: 80,
    congestionRisk: 'low',
    recommendations: []
  });

  const [editingZone, setEditingZone] = useState<Zone | null>(null);
  const [editForm, setEditForm] = useState<Partial<Zone>>({});

  useEffect(() => {
    if (activeTab === 'planning') {
      updateSimulationMetrics();
    }
  }, [zones, activeTab]);

  const updateSimulationMetrics = () => {
    const metrics: SimulationMetrics = {
      capacityDelta: calculateCapacityDelta(),
      costImpact: calculateCostImpact(),
      utilizationScore: calculateUtilizationScore(),
      collaborationScore: calculateCollaborationScore(),
      congestionRisk: calculateCongestionRisk(),
      recommendations: generateRecommendations()
    };
    setSimulationMetrics(metrics);
  };

  const calculateCapacityDelta = () => {
    const totalCapacity = zones.reduce((sum, zone) => sum + zone.capacity, 0);
    const totalOccupancy = zones.reduce((sum, zone) => sum + zone.currentOccupancy, 0);
    return ((totalCapacity - totalOccupancy) / totalCapacity) * 100;
  };

  const calculateCostImpact = () => {
    return zones.reduce((total, zone) => total + (zone.area * zone.costPerSqFt), 0);
  };

  const calculateUtilizationScore = () => {
    return zones.reduce((sum, zone) => sum + zone.utilization, 0) / zones.length;
  };

  const calculateCollaborationScore = () => {
    return departments.reduce((sum, dept) => sum + dept.collaborationScore, 0) / departments.length;
  };

  const calculateCongestionRisk = (): 'low' | 'medium' | 'high' => {
    const avgUtilization = calculateUtilizationScore();
    if (avgUtilization > 85) return 'high';
    if (avgUtilization > 70) return 'medium';
    return 'low';
  };

  const generateRecommendations = (): string[] => {
    const recommendations: string[] = [];
    const avgUtilization = calculateUtilizationScore();

    if (avgUtilization < 60) {
      recommendations.push('Consider consolidating underutilized spaces');
    }
    if (avgUtilization > 80) {
      recommendations.push('Evaluate expansion opportunities in high-traffic areas');
    }

    departments.forEach(dept => {
      if (dept.projectedGrowth > 10) {
        recommendations.push(`Plan for ${dept.name} department growth of ${dept.projectedGrowth}%`);
      }
    });

    return recommendations;
  };

  const handleZoneSelect = (zoneId: string) => {
    setSelectedZone(zoneId);
    const zone = zones.find(z => z.id === zoneId);
    if (zone) {
      setEditingZone(zone);
      setEditForm(zone);
    }
  };

  const handleZoneUpdate = (zoneId: string, updates: Partial<Zone>) => {
    setZones(zones.map(zone => 
      zone.id === zoneId ? { ...zone, ...updates } : zone
    ));
  };

  const handleInputChange = (field: keyof Zone, value: string | number) => {
    setEditForm(prev => ({
      ...prev,
      [field]: field === 'name' || field === 'type' ? value : Number(value)
    }));
  };

  const handleSaveChanges = () => {
    if (editingZone && editForm) {
      handleZoneUpdate(editingZone.id, editForm);
      setEditingZone(null);
      setEditForm({});
    }
  };

  const handleCancelEdit = () => {
    setEditingZone(null);
    setEditForm({});
    setSelectedZone(null);
  };

  const getCongestionColor = (risk: 'low' | 'medium' | 'high') => {
    switch (risk) {
      case 'high': return '#c33';
      case 'medium': return '#f59e0b';
      case 'low': return '#0A9B9B';
      default: return '#666';
    }
  };

  return (
    <div className="p-6 space-y-6" style={{ maxWidth: '1600px', margin: '0 auto' }}>
      <div className="mb-6">
        <h2 className="text-2xl font-bold mb-2" style={{ color: 'var(--sca-ink)' }}>
          Space Optimization
        </h2>
        <p style={{ color: 'var(--sca-muted)' }}>
          Building stack scenarios and planning simulator. Sample building data, illustrative
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 border-b" style={{ borderColor: 'var(--sca-border)' }}>
        <button
          onClick={() => setActiveTab('reclaim')}
          className="px-4 py-2 font-medium text-sm transition-all"
          style={{
            color: activeTab === 'reclaim' ? 'var(--sca-teal)' : 'var(--sca-muted)',
            background: 'transparent',
            border: 'none',
            borderBottom: activeTab === 'reclaim' ? '2px solid var(--sca-teal)' : 'none'
          }}
        >
          Reclaim Scenario
        </button>
        <button
          onClick={() => setActiveTab('planning')}
          className="px-4 py-2 font-medium text-sm transition-all"
          style={{
            color: activeTab === 'planning' ? 'var(--sca-teal)' : 'var(--sca-muted)',
            background: 'transparent',
            border: 'none',
            borderBottom: activeTab === 'planning' ? '2px solid var(--sca-teal)' : 'none'
          }}
        >
          Planning Simulator
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'reclaim' && (
        <>
          {/* Summary Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 rounded-lg border" style={{ backgroundColor: 'var(--sca-card)', borderColor: 'var(--sca-border)' }}>
              <div className="flex items-center gap-2 mb-2">
                <Maximize2 size={18} style={{ color: 'var(--sca-teal)' }} />
                <span className="text-sm" style={{ color: 'var(--sca-muted)' }}>Reclaimable Space</span>
              </div>
              <div className="text-3xl font-bold" style={{ color: 'var(--sca-ink)' }}>
                {reclaimSqft.toLocaleString()} sq ft
              </div>
              <div className="text-xs" style={{ color: 'var(--sca-muted)' }}>Floor 5 consolidation</div>
            </div>

            <div className="p-4 rounded-lg border" style={{ backgroundColor: 'var(--sca-card)', borderColor: 'var(--sca-border)' }}>
              <div className="flex items-center gap-2 mb-2">
                <DollarSign size={18} style={{ color: 'var(--sca-teal)' }} />
                <span className="text-sm" style={{ color: 'var(--sca-muted)' }}>Annual Savings</span>
              </div>
              <div className="text-3xl font-bold" style={{ color: 'var(--sca-ink)' }}>
                ${(annualSavings / 1000).toFixed(0)}K
              </div>
              <div className="text-xs" style={{ color: 'var(--sca-muted)' }}>at ${leaseRate}/sq ft (sample rate)</div>
            </div>

            <div className="p-4 rounded-lg border" style={{ backgroundColor: 'var(--sca-card)', borderColor: 'var(--sca-border)' }}>
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle size={18} style={{ color: 'var(--sca-teal)' }} />
                <span className="text-sm" style={{ color: 'var(--sca-muted)' }}>Peak Risk</span>
              </div>
              <div className="text-3xl font-bold" style={{ color: 'var(--sca-teal)' }}>
                Low
              </div>
              <div className="text-xs" style={{ color: 'var(--sca-muted)' }}>Wed peak protected with added focus</div>
            </div>
          </div>

          {/* Hero: Building Stack + Scenario Rail */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Stack Visualization */}
            <div className="lg:col-span-2 rounded-lg border p-6" style={{ backgroundColor: 'var(--sca-card)', borderColor: 'var(--sca-border)' }}>
              <div className="mb-4">
                <h3 className="text-xl font-semibold mb-2" style={{ color: 'var(--sca-ink)' }}>
                  Building Stack: Utilization Wash
                </h3>
                <p className="text-sm" style={{ color: 'var(--sca-muted)' }}>
                  Floor-by-floor view with 8-week utilization density. Darker teal = higher use.
                </p>
              </div>

              <div className="flex items-start justify-center">
                <BuildingStack
                  floors={scenario === 'current' ? currentFloors : proposedFloors}
                  width={500}
                  height={500}
                />
              </div>

              <div className="mt-4 flex items-center gap-6 text-xs" style={{ color: 'var(--sca-muted)' }}>
                <div className="flex items-center gap-2">
                  <div style={{ width: 16, height: 16, backgroundColor: '#F9F7F2', border: '1px solid #1a1a1a' }}></div>
                  <span>Empty / Reclaimed</span>
                </div>
                <div className="flex items-center gap-2">
                  <div style={{ width: 16, height: 16, backgroundColor: '#0A9B9B22' }}></div>
                  <span>&lt;40% util</span>
                </div>
                <div className="flex items-center gap-2">
                  <div style={{ width: 16, height: 16, backgroundColor: '#0A9B9B44' }}></div>
                  <span>40-55%</span>
                </div>
                <div className="flex items-center gap-2">
                  <div style={{ width: 16, height: 16, backgroundColor: '#0A9B9B66' }}></div>
                  <span>55-70%</span>
                </div>
                <div className="flex items-center gap-2">
                  <div style={{ width: 16, height: 16, backgroundColor: '#0A9B9B88' }}></div>
                  <span>&gt;70%</span>
                </div>
              </div>
            </div>

            {/* Scenario Rail */}
            <div className="space-y-4">
              {/* Scenario Toggle */}
              <div className="rounded-lg border p-4" style={{ backgroundColor: 'var(--sca-card)', borderColor: 'var(--sca-border)' }}>
                <div className="text-sm font-semibold mb-3" style={{ color: 'var(--sca-ink)' }}>
                  Compare Scenarios
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setScenario('current')}
                    className="flex-1 px-3 py-2 text-sm rounded-lg transition-colors"
                    style={{
                      backgroundColor: scenario === 'current' ? 'var(--sca-teal)' : 'white',
                      color: scenario === 'current' ? 'white' : 'var(--sca-ink)',
                      border: `1px solid ${scenario === 'current' ? 'var(--sca-teal)' : 'var(--sca-border)'}`
                    }}
                  >
                    Current
                  </button>
                  <button
                    onClick={() => setScenario('proposed')}
                    className="flex-1 px-3 py-2 text-sm rounded-lg transition-colors"
                    style={{
                      backgroundColor: scenario === 'proposed' ? 'var(--sca-teal)' : 'white',
                      color: scenario === 'proposed' ? 'white' : 'var(--sca-ink)',
                      border: `1px solid ${scenario === 'proposed' ? 'var(--sca-teal)' : 'var(--sca-border)'}`
                    }}
                  >
                    Proposed
                  </button>
                </div>
              </div>

              {/* Before/After */}
              <div className="rounded-lg border p-4" style={{ backgroundColor: '#fef9e7', borderColor: '#e8dfd0' }}>
                <div className="text-xs font-semibold mb-3" style={{ color: '#8a7355' }}>
                  {scenario === 'current' ? 'CURRENT STATE' : 'PROPOSED STATE'}
                </div>

                {scenario === 'current' ? (
                  <div className="space-y-3 text-sm">
                    <div>
                      <div className="text-xs" style={{ color: 'var(--sca-muted)' }}>Total Footprint</div>
                      <div className="font-semibold" style={{ color: 'var(--sca-ink)' }}>
                        {currentSqft.toLocaleString()} sq ft
                      </div>
                    </div>
                    <div>
                      <div className="text-xs" style={{ color: 'var(--sca-muted)' }}>Annual Lease</div>
                      <div className="font-semibold" style={{ color: 'var(--sca-ink)' }}>
                        ${((currentSqft * leaseRate) / 1000).toFixed(0)}K
                      </div>
                    </div>
                    <div>
                      <div className="text-xs" style={{ color: 'var(--sca-muted)' }}>Floor 5 Utilization</div>
                      <div className="font-semibold" style={{ color: '#c33' }}>
                        38% (8 weeks)
                      </div>
                    </div>
                    <div>
                      <div className="text-xs" style={{ color: 'var(--sca-muted)' }}>Wed Peak Seats</div>
                      <div className="font-semibold" style={{ color: 'var(--sca-ink)' }}>
                        248 available
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3 text-sm">
                    <div>
                      <div className="text-xs" style={{ color: 'var(--sca-muted)' }}>Total Footprint</div>
                      <div className="font-semibold" style={{ color: 'var(--sca-teal)' }}>
                        {proposedSqft.toLocaleString()} sq ft
                      </div>
                    </div>
                    <div>
                      <div className="text-xs" style={{ color: 'var(--sca-muted)' }}>Annual Lease</div>
                      <div className="font-semibold" style={{ color: 'var(--sca-teal)' }}>
                        ${((proposedSqft * leaseRate) / 1000).toFixed(0)}K
                      </div>
                    </div>
                    <div>
                      <div className="text-xs" style={{ color: 'var(--sca-muted)' }}>Floor 5 Reclaimed</div>
                      <div className="font-semibold" style={{ color: 'var(--sca-teal)' }}>
                        8,500 sq ft
                      </div>
                    </div>
                    <div>
                      <div className="text-xs" style={{ color: 'var(--sca-muted)' }}>Wed Peak Seats</div>
                      <div className="font-semibold" style={{ color: 'var(--sca-teal)' }}>
                        252 available
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Lease Rate Demo */}
              <div className="rounded-lg border p-4" style={{ backgroundColor: 'var(--sca-card)', borderColor: 'var(--sca-border)' }}>
                <div className="text-xs font-semibold mb-3" style={{ color: 'var(--sca-ink)' }}>
                  DEMO ASSUMPTION (editable)
                </div>
                <div className="mb-2">
                  <label className="text-xs" style={{ color: 'var(--sca-muted)' }}>Lease Rate ($/sq ft)</label>
                  <input
                    type="number"
                    value={leaseRate}
                    onChange={(e) => setLeaseRate(parseInt(e.target.value) || 42)}
                    className="w-full mt-1 px-3 py-2 rounded border text-sm"
                    style={{ borderColor: 'var(--sca-border)' }}
                  />
                </div>
                <div className="text-xs" style={{ color: 'var(--sca-muted)' }}>
                  Sample rate for calculation demo. Adjust to local market.
                </div>
              </div>

              {/* Investment Note */}
              <div className="rounded-lg p-4" style={{ backgroundColor: 'var(--sca-badge-bg)' }}>
                <div className="text-xs font-semibold mb-2" style={{ color: 'var(--sca-ink)' }}>
                  Reinvestment
                </div>
                <div className="text-xs" style={{ color: 'var(--sca-muted)' }}>
                  Add 12 focus pods on Floor 2 ($42K) to protect Wed peak experience. Net savings: ${((annualSavings - 42000) / 1000).toFixed(0)}K/year.
                </div>
              </div>
            </div>
          </div>

          {/* Space-Type Matrix */}
          <div className="rounded-lg border p-6" style={{ backgroundColor: 'var(--sca-card)', borderColor: 'var(--sca-border)' }}>
            <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--sca-ink)' }}>
              Space-Type Performance Matrix
            </h3>
            <p className="text-sm mb-4" style={{ color: 'var(--sca-muted)' }}>
              How each workspace type performs before and after consolidation. Proposed scenario improves utilization across all types.
            </p>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ borderBottom: '2px solid var(--sca-border)' }}>
                    <th className="text-left py-3 px-4" style={{ color: 'var(--sca-ink)', fontWeight: '600' }}>Space Type</th>
                    <th className="text-right py-3 px-4" style={{ color: 'var(--sca-ink)', fontWeight: '600' }}>Current Count</th>
                    <th className="text-right py-3 px-4" style={{ color: 'var(--sca-ink)', fontWeight: '600' }}>Current Util</th>
                    <th className="text-right py-3 px-4" style={{ color: 'var(--sca-ink)', fontWeight: '600' }}>Proposed Count</th>
                    <th className="text-right py-3 px-4" style={{ color: 'var(--sca-ink)', fontWeight: '600' }}>Proposed Util</th>
                    <th className="text-right py-3 px-4" style={{ color: 'var(--sca-ink)', fontWeight: '600' }}>Impact</th>
                  </tr>
                </thead>
                <tbody>
                  {spaceTypes.map((type, idx) => {
                    const utilDelta = type.proposedUtil - type.currentUtil;
                    const countDelta = type.proposed - type.current;
                    
                    return (
                      <tr
                        key={idx}
                        style={{ borderBottom: '1px solid var(--sca-border)' }}
                      >
                        <td className="py-3 px-4 font-medium" style={{ color: 'var(--sca-ink)' }}>
                          {type.type}
                        </td>
                        <td className="py-3 px-4 text-right" style={{ color: 'var(--sca-ink)' }}>
                          {type.current}
                        </td>
                        <td className="py-3 px-4 text-right">
                          <span style={{ color: type.currentUtil < 50 ? '#c33' : '#8a7355' }}>
                            {type.currentUtil}%
                          </span>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <span style={{ color: 'var(--sca-ink)' }}>
                            {type.proposed}
                          </span>
                          {countDelta !== 0 && (
                            <span className="ml-2 text-xs" style={{ color: countDelta > 0 ? 'var(--sca-teal)' : '#c33' }}>
                              ({countDelta > 0 ? '+' : ''}{countDelta})
                            </span>
                          )}
                        </td>
                        <td className="py-3 px-4 text-right">
                          <span style={{ color: 'var(--sca-teal)' }}>
                            {type.proposedUtil}%
                          </span>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <span style={{ color: utilDelta > 0 ? 'var(--sca-teal)' : '#c33' }}>
                            {utilDelta > 0 ? '+' : ''}{utilDelta}%
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* CFO Memo Preview */}
          <div className="rounded-lg border p-6" style={{ backgroundColor: '#fef9e7', borderColor: '#e8dfd0' }}>
            <h3 className="text-lg font-semibold mb-3" style={{ color: 'var(--sca-ink)' }}>
              CFO Restack Memo Summary
            </h3>
            <div className="space-y-3 text-sm" style={{ color: 'var(--sca-ink)' }}>
              <p>
                <strong>Recommendation:</strong> Consolidate Floor 5 operations to Floors 2-4. Reclaim 8,500 sq ft for sublet or surrender at next lease event.
              </p>
              <p>
                <strong>Evidence:</strong> Floor 5 sustained 38% utilization over 8 weeks with 34-week vacancy streak. Peak Wednesday capacity remains protected at 252 seats (vs 248 current).
              </p>
              <p>
                <strong>Financial:</strong> Annual savings of ${(annualSavings / 1000).toFixed(0)}K at ${leaseRate}/sq ft lease rate. Reinvestment of $42K for focus pods maintains peak-day experience.
              </p>
              <p>
                <strong>Risk:</strong> Low. Proposed densification improves space-type utilization from 52% (open desks) to 68%, and meeting rooms from 42% to 72%. Floor sensor data provides 60-day confidence window.
              </p>
            </div>
          </div>
        </>
      )}

      {activeTab === 'planning' && (
        <>
          {/* Simulation Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="rounded-lg border p-4" style={{ backgroundColor: 'var(--sca-card)', borderColor: 'var(--sca-border)' }}>
              <div className="flex items-center mb-2">
                <Users size={18} style={{ color: 'var(--sca-teal)', marginRight: 8 }} />
                <h3 className="font-semibold" style={{ color: 'var(--sca-ink)' }}>Capacity Impact</h3>
              </div>
              <p className="text-3xl font-bold" style={{ color: 'var(--sca-ink)' }}>{simulationMetrics.capacityDelta.toFixed(1)}%</p>
              <p className="text-sm" style={{ color: 'var(--sca-muted)' }}>Available capacity</p>
            </div>

            <div className="rounded-lg border p-4" style={{ backgroundColor: 'var(--sca-card)', borderColor: 'var(--sca-border)' }}>
              <div className="flex items-center mb-2">
                <DollarSign size={18} style={{ color: 'var(--sca-teal)', marginRight: 8 }} />
                <h3 className="font-semibold" style={{ color: 'var(--sca-ink)' }}>Cost Impact</h3>
              </div>
              <p className="text-3xl font-bold" style={{ color: 'var(--sca-ink)' }}>${simulationMetrics.costImpact.toLocaleString()}</p>
              <p className="text-sm" style={{ color: 'var(--sca-muted)' }}>Monthly operating cost</p>
            </div>

            <div className="rounded-lg border p-4" style={{ backgroundColor: 'var(--sca-card)', borderColor: 'var(--sca-border)' }}>
              <div className="flex items-center mb-2">
                <AlertTriangle size={18} style={{ color: 'var(--sca-teal)', marginRight: 8 }} />
                <h3 className="font-semibold" style={{ color: 'var(--sca-ink)' }}>Congestion Risk</h3>
              </div>
              <p className="text-3xl font-bold" style={{ color: getCongestionColor(simulationMetrics.congestionRisk) }}>
                {simulationMetrics.congestionRisk.charAt(0).toUpperCase() + simulationMetrics.congestionRisk.slice(1)}
              </p>
              <p className="text-sm" style={{ color: 'var(--sca-muted)' }}>Based on current layout</p>
            </div>
          </div>

          {/* Zone Management */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <div className="rounded-lg border p-4" style={{ backgroundColor: 'var(--sca-card)', borderColor: 'var(--sca-border)' }}>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold" style={{ color: 'var(--sca-ink)' }}>Zone Configuration</h3>
                  <p className="text-sm" style={{ color: 'var(--sca-muted)' }}>Click a zone to edit</p>
                </div>
                <div className="space-y-4">
                  {zones.map(zone => (
                    <div 
                      key={zone.id}
                      className="p-4 border rounded-lg cursor-pointer transition-colors"
                      style={{
                        borderColor: selectedZone === zone.id ? 'var(--sca-teal)' : 'var(--sca-border)',
                        backgroundColor: selectedZone === zone.id ? 'var(--sca-badge-bg)' : 'transparent'
                      }}
                      onClick={() => handleZoneSelect(zone.id)}
                    >
                      <div className="flex justify-between items-center">
                        <div>
                          <h4 className="font-semibold" style={{ color: 'var(--sca-ink)' }}>{zone.name}</h4>
                          <p className="text-sm" style={{ color: 'var(--sca-muted)' }}>{zone.type}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold" style={{ color: 'var(--sca-ink)' }}>{zone.currentOccupancy}/{zone.capacity}</p>
                          <p className="text-sm" style={{ color: 'var(--sca-muted)' }}>{zone.utilization}% utilized</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Zone Editor Panel */}
            <div className="rounded-lg border p-4" style={{ backgroundColor: 'var(--sca-card)', borderColor: 'var(--sca-border)' }}>
              {editingZone ? (
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-semibold" style={{ color: 'var(--sca-ink)' }}>Edit Zone</h3>
                    <button
                      onClick={handleCancelEdit}
                      style={{ color: 'var(--sca-muted)', background: 'none', border: 'none', cursor: 'pointer' }}
                    >
                      <X size={20} />
                    </button>
                  </div>
                  
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium mb-1" style={{ color: 'var(--sca-ink)' }}>
                        Zone Name
                      </label>
                      <input
                        type="text"
                        value={editForm.name || ''}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                        className="w-full px-3 py-2 border rounded-md"
                        style={{ borderColor: 'var(--sca-border)' }}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1" style={{ color: 'var(--sca-ink)' }}>
                        Type
                      </label>
                      <select
                        value={editForm.type || ''}
                        onChange={(e) => handleInputChange('type', e.target.value)}
                        className="w-full px-3 py-2 border rounded-md"
                        style={{ borderColor: 'var(--sca-border)' }}
                      >
                        <option value="workspace">Workspace</option>
                        <option value="meeting">Meeting</option>
                        <option value="collaboration">Collaboration</option>
                        <option value="amenity">Amenity</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1" style={{ color: 'var(--sca-ink)' }}>
                        Capacity
                      </label>
                      <input
                        type="number"
                        value={editForm.capacity || ''}
                        onChange={(e) => handleInputChange('capacity', e.target.value)}
                        className="w-full px-3 py-2 border rounded-md"
                        style={{ borderColor: 'var(--sca-border)' }}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1" style={{ color: 'var(--sca-ink)' }}>
                        Area (sq ft)
                      </label>
                      <input
                        type="number"
                        value={editForm.area || ''}
                        onChange={(e) => handleInputChange('area', e.target.value)}
                        className="w-full px-3 py-2 border rounded-md"
                        style={{ borderColor: 'var(--sca-border)' }}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1" style={{ color: 'var(--sca-ink)' }}>
                        Cost per Sq Ft
                      </label>
                      <input
                        type="number"
                        value={editForm.costPerSqFt || ''}
                        onChange={(e) => handleInputChange('costPerSqFt', e.target.value)}
                        className="w-full px-3 py-2 border rounded-md"
                        style={{ borderColor: 'var(--sca-border)' }}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1" style={{ color: 'var(--sca-ink)' }}>
                        Current Occupancy
                      </label>
                      <input
                        type="number"
                        value={editForm.currentOccupancy || ''}
                        onChange={(e) => handleInputChange('currentOccupancy', e.target.value)}
                        className="w-full px-3 py-2 border rounded-md"
                        style={{ borderColor: 'var(--sca-border)' }}
                      />
                    </div>

                    <button
                      onClick={handleSaveChanges}
                      className="w-full py-2 px-4 rounded-md flex items-center justify-center space-x-2"
                      style={{
                        backgroundColor: 'var(--sca-teal)',
                        color: 'white',
                        border: 'none',
                        cursor: 'pointer'
                      }}
                    >
                      <Save size={16} />
                      <span>Save Changes</span>
                    </button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8" style={{ color: 'var(--sca-muted)' }}>
                  Select a zone to edit its configuration
                </div>
              )}
            </div>
          </div>

          {/* Recommendations */}
          <div className="rounded-lg border p-4" style={{ backgroundColor: 'var(--sca-card)', borderColor: 'var(--sca-border)' }}>
            <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--sca-ink)' }}>Recommendations</h3>
            <div className="space-y-4">
              {simulationMetrics.recommendations.map((recommendation, index) => (
                <div key={index} className="flex items-start space-x-2">
                  <TrendingUp size={18} style={{ color: 'var(--sca-teal)', marginTop: 2 }} />
                  <p className="text-sm" style={{ color: 'var(--sca-ink)' }}>{recommendation}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Department Growth Projections */}
          <div className="rounded-lg border p-4" style={{ backgroundColor: 'var(--sca-card)', borderColor: 'var(--sca-border)' }}>
            <h3 className="text-lg font-semibold mb-4" style={{ color: 'var(--sca-ink)' }}>Department Growth Projections</h3>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart
                data={departments.map(dept => ({
                  name: dept.name,
                  current: dept.headcount,
                  projected: dept.headcount * (1 + dept.projectedGrowth / 100)
                }))}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e8e4dc" />
                <XAxis dataKey="name" stroke="#666" style={{ fontSize: '12px' }} />
                <YAxis stroke="#666" style={{ fontSize: '12px' }} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#F9F7F2', 
                    border: '1px solid #e8e4dc',
                    borderRadius: '8px'
                  }} 
                />
                <Legend />
                <Area type="monotone" dataKey="current" stackId="1" stroke="#0A9B9B" fill="#0A9B9B" fillOpacity={0.4} name="Current Headcount" />
                <Area type="monotone" dataKey="projected" stackId="2" stroke="#8a7355" fill="#8a7355" fillOpacity={0.4} name="Projected Headcount" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </>
      )}
    </div>
  );
};

export default SpaceOptimization;
